import * as Analytics from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import React, { useCallback, useContext, useMemo, useRef, useState, ComponentProps, useImperativeHandle, forwardRef } from 'react'
import { useIntl } from 'react-intl'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import useToggle from 'hooks/useToggle'
import FormattedCruiseLineSearchSummary from '../CruiseLineSelect/FormattedCruiseLineSearchSummary'
import CruiseLocationContent from './CruiseLocationContent'
import useGlobalSearchTypeahead from 'hooks/GlobalSearch/useGlobalSearchTypeahead'
import { TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES, TOUR_V2_SEARCH_TYPES } from 'constants/tours'
import FilterSearchInput from 'components/Common/FilterPanel/FilterSearchInput'
import CruiseFilterInput from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseFilterInput'
const SET_SEARCH_ITEMS = GlobalSearchStateActions.SET_SEARCH_ITEMS

export interface CruiseLocationDesktopInputRef {
  focus: () => void
}

interface Props {
  dropdownAnchorRef: React.RefObject<HTMLElement | null>
  dropdownSize?: ComponentProps<typeof DropdownSheet>['size']
  isChipMode?: boolean
  hideImages?: boolean
  initialFilters?: App.CruiseInitialFilters
  hideTrendOptions?: boolean
  shouldIgnoreFlashOffers?: boolean
  onChange: (values: App.CruiseGlobalFilters) => void
}

const CruiseLocationDesktopInput = forwardRef<CruiseLocationDesktopInputRef, Props>(({
  dropdownAnchorRef,
  onChange,
  isChipMode,
  dropdownSize,
  hideImages,
  initialFilters,
  hideTrendOptions,
  shouldIgnoreFlashOffers,
}, ref) => {
  const intl = useIntl()
  const inputTriggerRef = useRef<HTMLInputElement>(null)

  const globalSearchState = useContext(GlobalSearchStateContext)
  const { searchItems = [], suggestedSearchItems } = globalSearchState
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const [searchTerm, setSearchTerm] = useState('')
  const [isAllSelected, setIsAllSelected] = useState(false)
  const [isOpen, , show, close] = useToggle(false)

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputTriggerRef.current?.focus()
      show()
    },
  }), [show])

  const [facets] = useCruiseSearchFacets({ facetTypes: ['destination_ports'], ...(initialFilters || {}) })
  useGlobalSearchTypeahead({
    search: searchTerm,
    searchTypes: TOUR_V2_SEARCH_TYPES,
    typeaheadTypes: TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES,
  })

  const subtitle = useMemo(() => {
    if (searchTerm) {
      return intl.formatMessage(
        {
          id: 'RQB1+Ryuo/',
          defaultMessage: 'Search results for "{searchTerm}"',
          description: 'Caption showing search results for a specific search term',
        },
        { searchTerm },
      )
    }
    return intl.formatMessage({
      id: 'Ljpt81JMsa',
      defaultMessage: 'or browse by popular regions',
      description: 'Caption suggesting browsing popular regions',
    })
  }, [intl, searchTerm])

  const locations = useMemo(() => {
    if (!searchTerm) {
      return hideTrendOptions ? facets.filter(({ count }) => !!count) : facets.filter(({ isTrend }) => isTrend)
    }

    let filtered = facets.filter(({ name }) => name.toLowerCase().includes(searchTerm.toLowerCase()))
    if (!filtered.length) {
      filtered = facets.filter(({ name }) => {
        return suggestedSearchItems?.find((suggestedValue) => {
          return name.includes(suggestedValue?.format?.secondaryText!)
        })
      })
    }
    return filtered.slice(0, 100)
  }, [searchTerm, facets, suggestedSearchItems, hideTrendOptions])

  const inputValue = useMemo(() => {
    if (isOpen) return searchTerm
    if (searchItems.length > 1) {
      const names = searchItems.slice(0, 1).map((item) => item.format.mainText).join(', ')
      return `${names} + ${searchItems.length - 1}`
    }
    return searchItems.map((item) => item.format.mainText).join(', ')
  }, [searchItems, isOpen, searchTerm])

  const handleOnApply = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_applied',
      action: 'applied',
      category: 'cruise_location_filter',
      type: 'interaction',
    })

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts: searchItems.map(item => ({
        label: 'Destination',
        type: 'destination',
        value: item.value,
      })),
    }))

    onChange({ searchItems })
    close()
  }, [onChange, searchItems, close, globalSearchState])

  const handleOnClear = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_cleared',
      action: 'cleared',
      category: 'cruise_location_filter',
      type: 'interaction',
    })
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems: [] })
    setSearchTerm('')
    setIsAllSelected(false)
  }, [searchDispatch])

  const handleOnChange = useCallback((searchItems: Array<App.SearchItem>) => {
    Analytics.trackClientEvent({
      subject: 'filter_item_selected',
      action: 'selected',
      category: 'cruise_location_filter',
      type: 'interaction',
    })
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems })
    setIsAllSelected(false)
  }, [searchDispatch])

  const handleSelectAll = useCallback(() => {
    Analytics.trackClientEvent({
      subject: isAllSelected ? 'filter_all_unselected' : 'filter_all_selected',
      action: isAllSelected ? 'unselected' : 'selected',
      category: 'cruise_location_filter',
      type: 'interaction',
    })
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems: [] })
    setIsAllSelected(!isAllSelected)
  }, [searchDispatch, isAllSelected])

  const handlerSearchTerm = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const handlerClearSearchTerm = useCallback(() => setSearchTerm(''), [])

  const cruisingToLabel = intl.formatMessage({
    id: 'L8sC3uKwxa',
    defaultMessage: 'Cruising to',
    description: 'Label for cruise destination filter',
  })

  const allDestinationsPlaceholder = intl.formatMessage({
    id: 'mgP9opd4uz',
    defaultMessage: 'All destinations',
    description: 'Placeholder text when all destinations are selected',
  })

  const searchDestinationsPlaceholder = intl.formatMessage({
    id: 'DZhQjwmkUP',
    defaultMessage: 'Search destinations',
    description: 'Placeholder text for destination search input',
  })

  const applyButtonText = intl.formatMessage({
    id: 'jdplGTjcg6',
    defaultMessage: 'Apply',
    description: 'Apply button text for cruise filters',
  })

  const resetAllButtonText = intl.formatMessage({
    id: 'elXXkD76nK',
    defaultMessage: 'Reset all',
    description: 'Reset all button text for cruise filters',
  })

  return <>
    <CruiseFilterInput
      isOpen={isOpen}
      label={cruisingToLabel}
      placeholder={isOpen ? searchDestinationsPlaceholder : allDestinationsPlaceholder}
      inputValue={inputValue}
      data-testid="cruise-location-filter-input"
      formFieldRef={inputTriggerRef}
      selectedItems={searchItems.length}
      onFocus={show}
      onClick={() => {
        Analytics.trackClientEvent({
          subject: 'filter_opened',
          action: 'opened',
          category: 'cruise_location_filter',
          type: 'interaction',
        })
        show()
      }}
      handleSearchTerm={handlerSearchTerm}
      handlerClearSearchTerm={handlerClearSearchTerm}
      isChipMode={isChipMode}
    />

    <DropdownSheet
      size={dropdownSize || 'fill-anchor'}
      anchorRef={dropdownAnchorRef}
      triggerRef={inputTriggerRef}
      open={isOpen}
      onClose={close}
      title={isChipMode ? cruisingToLabel : undefined}
      subtitle={!isChipMode ? subtitle : undefined}
      hasDismissButton={isChipMode}
      data-testid="cruise-location-select-dropdown"
      headerExtension={isChipMode && <FilterSearchInput
        placeholder={searchDestinationsPlaceholder}
        onChange={setSearchTerm}
        value={inputValue}
      />}
      primaryActionProps={{
        'data-testid': 'cruise-filter-footer-apply',
        children: applyButtonText,
        onClick: handleOnApply,
      }}
      secondaryActionProps={{
        'data-testid': 'cruise-filter-footer-clear-all',
        kind: 'tertiary',
        children: resetAllButtonText,
        onClick: handleOnClear,
      }}
      footerStart={!isChipMode && <FormattedCruiseLineSearchSummary
        initialFilters={initialFilters}
        shouldIgnoreFlashOffers={shouldIgnoreFlashOffers}
      />}
    >
      <CruiseLocationContent
        locations={locations}
        searchTerm={searchTerm}
        searchItems={searchItems}
        isAllSelected={isAllSelected}
        onChange={handleOnChange}
        onSelectAll={handleSelectAll}
        hideImages={hideImages}
        initialFilters={initialFilters}
      />
    </DropdownSheet>
  </>
})

CruiseLocationDesktopInput.displayName = 'CruiseLocationDesktopInput'

export default CruiseLocationDesktopInput
